/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

export const FACET_DRAFT_RECOVERY_VERSION = 1;

const EMPTY_DELTA = {
  assignmentChanges: {},
  clearAssignmentIds: {},
  deleteGroupIds: {},
  clearAllAssignments: false,
};

const requestState = () => ({ requestId: null, status: 'idle', error: null });
const clone = value => (value == null ? value : JSON.parse(JSON.stringify(value)));
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const initialState = {
  scope: null,
  catalog: { items: [], nextCursor: null, ...requestState() },
  openPolicy: {
    facetKey: null,
    baseline: null,
    draft: null,
    ...requestState(),
  },
  values: {
    entities: {},
    pages: {},
    pageOrder: [],
    nextCursor: null,
    ...requestState(),
  },
  delta: clone(EMPTY_DELTA),
  save: {
    requestId: null,
    status: 'idle',
    error: null,
    mutationId: null,
    fingerprint: null,
  },
  recovery: { restored: false },
};

const freshState = scope => ({ ...clone(initialState), scope: clone(scope) });
const SHOP_SCOPE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const LANGUAGE_SCOPE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/;

function validScope(scope) {
  return scope
    && typeof scope.shop === 'string'
    && SHOP_SCOPE_PATTERN.test(scope.shop)
    && typeof scope.language === 'string'
    && LANGUAGE_SCOPE_PATTERN.test(scope.language)
    && scope.document_type === 'product';
}

function sameScope(left, right) {
  return validScope(left) && validScope(right)
    && left.shop === right.shop
    && left.language === right.language
    && left.document_type === right.document_type;
}

function sanitizeVisual(visual) {
  if (!visual || typeof visual !== 'object') return null;
  return { kind: visual.kind, value: visual.value };
}

function sanitizeValuePolicy(valuePolicy) {
  const source = valuePolicy && typeof valuePolicy === 'object' ? valuePolicy : {};
  const result = {
    unmapped: source.unmapped,
    sort_remaining_by: source.sort_remaining_by,
    pinned: Array.isArray(source.pinned) ? source.pinned.map(String) : [],
    hidden: Array.isArray(source.hidden) ? source.hidden.map(String) : [],
    groups: Array.isArray(source.groups) ? source.groups.map(group => ({
      value: String(group.value),
      label: group.label,
      visual: sanitizeVisual(group.visual),
    })) : [],
  };
  if (typeof source.presentation === 'string') {
    result.presentation = source.presentation;
  }
  if (source.numeric && typeof source.numeric === 'object') {
    result.numeric = {
      desired_type: source.numeric.desired_type,
      fallback_type: source.numeric.fallback_type,
      numeric_definition_revision: source.numeric.numeric_definition_revision,
      state: source.numeric.state,
    };
  }
  return result;
}

function sanitizePolicy(policy) {
  const source = policy && typeof policy === 'object' ? policy : {};
  return {
    title: source.title,
    type: source.type,
    visible: source.visible,
    searchable: source.searchable,
    value_policy: sanitizeValuePolicy(source.value_policy),
  };
}

function changedPolicy(draft, baseline) {
  const next = sanitizePolicy(draft);
  const previous = sanitizePolicy(baseline);
  return Object.keys(next).reduce((patch, key) => {
    if (JSON.stringify(stableValue(next[key])) !== JSON.stringify(stableValue(previous[key]))) {
      patch[key] = next[key];
    }
    return patch;
  }, {});
}

function sortedKeys(map) {
  return Object.keys(map || {}).sort();
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value).sort().reduce((result, key) => {
    result[key] = stableValue(value[key]);
    return result;
  }, {});
}

function mutationCore(state) {
  const changes = sortedKeys(state.delta.assignmentChanges).map(sourceValueId => ({
    source_value_id: sourceValueId,
    canonical_value: state.delta.assignmentChanges[sourceValueId],
  }));
  return {
    facet_key: state.openPolicy.facetKey,
    expected_revision: state.openPolicy.baseline ? state.openPolicy.baseline.revision : null,
    policy: changedPolicy(
      state.openPolicy.draft,
      state.openPolicy.baseline ? state.openPolicy.baseline.policy : null,
    ),
    assignment_changes: changes,
    clear_assignment_ids: sortedKeys(state.delta.clearAssignmentIds),
    delete_group_ids: sortedKeys(state.delta.deleteGroupIds),
    clear_all_assignments: state.delta.clearAllAssignments,
    validation_token: null,
  };
}

function fingerprint(state) {
  return JSON.stringify(stableValue(mutationCore(state)));
}

function mutationId() {
  return `facet-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function resetEdits(state, policy, revision) {
  state.openPolicy.baseline = { policy: clone(policy), revision };
  state.openPolicy.draft = clone(policy);
  state.delta = clone(EMPTY_DELTA);
  state.save = clone(initialState.save);
  state.recovery = { restored: false };
}

const slice = createSlice({
  name: 'facetPolicyDraft',
  initialState,
  reducers: {
    scopeOpened(state, { payload }) {
      if (!validScope(payload)) return freshState(null);
      if (!sameScope(state.scope, payload)) return freshState(payload);
      return undefined;
    },
    catalogRequested(state, { payload }) {
      state.catalog.requestId = payload.requestId;
      state.catalog.status = 'loading';
      state.catalog.error = null;
    },
    catalogSucceeded(state, { payload }) {
      if (state.catalog.requestId !== payload.requestId) return;
      state.catalog.items = clone(payload.items || []);
      state.catalog.nextCursor = payload.nextCursor || null;
      state.catalog.status = 'succeeded';
    },
    catalogFailed(state, { payload }) {
      if (state.catalog.requestId !== payload.requestId) return;
      state.catalog.status = 'failed';
      state.catalog.error = payload.error;
    },
    policyRequested(state, { payload }) {
      state.openPolicy.requestId = payload.requestId;
      state.openPolicy.status = 'loading';
      state.openPolicy.error = null;
    },
    policySucceeded(state, { payload }) {
      if (state.openPolicy.requestId !== payload.requestId) return;
      state.openPolicy.facetKey = String(payload.facetKey);
      state.openPolicy.status = 'succeeded';
      resetEdits(state, payload.policy, payload.revision);
      state.values = clone(initialState.values);
    },
    policyFailed(state, { payload }) {
      if (state.openPolicy.requestId !== payload.requestId) return;
      state.openPolicy.status = 'failed';
      state.openPolicy.error = payload.error;
    },
    draftPatched(state, { payload }) {
      if (!state.openPolicy.draft) return;
      state.openPolicy.draft = { ...state.openPolicy.draft, ...clone(payload) };
    },
    facetTypeChanged(state, { payload }) {
      const type = payload && payload.type;
      if (!state.openPolicy.draft || !['string', 'boolean', 'color', 'number'].includes(type)) return;
      if (type === 'boolean'
        && (typeof payload.trueLabel !== 'string' || typeof payload.falseLabel !== 'string')) return;
      const baseline = state.openPolicy.baseline && state.openPolicy.baseline.policy;
      state.openPolicy.draft.type = type;
      state.openPolicy.draft.searchable = type === 'string';
      if (baseline && baseline.type === type) {
        state.openPolicy.draft.value_policy = clone(baseline.value_policy);
        state.openPolicy.draft.searchable = baseline.searchable;
        state.delta = clone(EMPTY_DELTA);
        return;
      }
      state.openPolicy.draft.value_policy = {
        unmapped: type === 'string' ? 'pass_through' : 'hidden',
        sort_remaining_by: 'count_desc',
        pinned: [],
        hidden: [],
        groups: type === 'boolean' ? [
          { value: 'true', label: payload.trueLabel, visual: null },
          { value: 'false', label: payload.falseLabel, visual: null },
        ] : [],
      };
      state.delta = { ...clone(EMPTY_DELTA), clearAllAssignments: true };
      state.save = clone(initialState.save);
    },
    valuePolicyPatched(state, { payload }) {
      if (!state.openPolicy.draft) return;
      state.openPolicy.draft.value_policy = {
        ...(state.openPolicy.draft.value_policy || {}),
        ...clone(payload),
      };
    },
    valuesPageRequested(state, { payload }) {
      state.values.requestId = payload.requestId;
      state.values.status = 'loading';
      state.values.error = null;
    },
    valuesPageSucceeded(state, { payload }) {
      if (state.values.requestId !== payload.requestId) return;
      const pageKey = payload.cursor || '__first__';
      const ids = (payload.items || []).map((item) => {
        const id = String(item.source_value_id);
        state.values.entities[id] = clone(item);
        state.values.entities[id].source_value_id = id;
        return id;
      });
      state.values.pages[pageKey] = ids;
      if (state.values.pageOrder.indexOf(pageKey) === -1) state.values.pageOrder.push(pageKey);
      state.values.nextCursor = payload.nextCursor || null;
      state.values.status = 'succeeded';
    },
    valuesPageFailed(state, { payload }) {
      if (state.values.requestId !== payload.requestId) return;
      state.values.status = 'failed';
      state.values.error = payload.error;
    },
    assignmentChanged(state, { payload }) {
      const id = String(payload.sourceValueId);
      state.delta.assignmentChanges[id] = String(payload.canonicalValue);
      delete state.delta.clearAssignmentIds[id];
    },
    assignmentCleared(state, { payload }) {
      const id = String(payload.sourceValueId);
      delete state.delta.assignmentChanges[id];
      if (!state.delta.clearAllAssignments) state.delta.clearAssignmentIds[id] = true;
    },
    groupDeleted(state, { payload }) {
      const groupId = String(payload.groupId);
      state.delta.deleteGroupIds[groupId] = true;
      const policy = state.openPolicy.draft && state.openPolicy.draft.value_policy;
      if (policy && Array.isArray(policy.groups)) {
        policy.groups = policy.groups.filter(group => String(group.value) !== groupId);
      }
      const affected = (payload.sourceValueIds || []).map(String);
      Object.keys(state.delta.assignmentChanges).forEach((id) => {
        if (state.delta.assignmentChanges[id] === groupId) affected.push(id);
      });
      Object.keys(state.values.entities).forEach((id) => {
        const value = state.values.entities[id];
        if (String(value.canonical_value) === groupId) affected.push(id);
      });
      affected.forEach((id) => {
        delete state.delta.assignmentChanges[id];
        if (!state.delta.clearAllAssignments) state.delta.clearAssignmentIds[id] = true;
      });
    },
    assignmentsClearedAll(state) {
      state.delta.assignmentChanges = {};
      state.delta.clearAssignmentIds = {};
      state.delta.clearAllAssignments = true;
    },
    saveRequested: {
      reducer(state, { payload }) {
        if (!state.openPolicy.baseline || !state.openPolicy.draft) return;
        const nextFingerprint = fingerprint(state);
        if (state.save.fingerprint !== nextFingerprint || !state.save.mutationId) {
          state.save.mutationId = payload.mutationId;
          state.save.fingerprint = nextFingerprint;
        }
        state.save.requestId = payload.requestId;
        state.save.status = 'saving';
        state.save.error = null;
      },
      prepare(payload) {
        return { payload: { ...payload, mutationId: payload.mutationId || mutationId() } };
      },
    },
    saveSucceeded(state, { payload }) {
      if (state.save.requestId !== payload.requestId) return;
      resetEdits(state, payload.policy, payload.revision);
      state.openPolicy.status = 'succeeded';
    },
    saveFailed(state, { payload }) {
      if (state.save.requestId !== payload.requestId) return;
      state.save.status = payload.code === 'REVISION_CONFLICT'
        || payload.code === 'IDEMPOTENCY_WINDOW_EXPIRED' ? 'recoverable' : 'failed';
      state.save.error = { code: payload.code, message: payload.message };
    },
    saveErrorDismissed(state) {
      state.save.error = null;
      if (state.save.status === 'failed') state.save.status = 'idle';
    },
    conflictRebased(state, { payload }) {
      if (state.save.requestId !== payload.requestId || !state.openPolicy.baseline
        || !state.openPolicy.draft || !payload.policy) return;
      const localPatch = changedPolicy(
        state.openPolicy.draft,
        state.openPolicy.baseline.policy,
      );
      state.openPolicy.baseline = {
        policy: clone(payload.policy),
        revision: payload.revision,
      };
      state.openPolicy.draft = { ...clone(payload.policy), ...clone(localPatch) };
      state.save.status = 'recoverable';
      state.save.mutationId = null;
      state.save.fingerprint = null;
    },
    discarded(state) {
      if (!state.openPolicy.baseline) return;
      state.openPolicy.draft = clone(state.openPolicy.baseline.policy);
      state.delta = clone(EMPTY_DELTA);
      state.save = clone(initialState.save);
      state.recovery = { restored: false };
    },
    recoveryRestored(state, { payload }) {
      if (!payload || payload.version !== FACET_DRAFT_RECOVERY_VERSION
        || !sameScope(state.scope, payload.scope)) return;
      state.openPolicy.facetKey = payload.facetKey;
      // Policy load completes before recovery restore. Keep latest server baseline/revision:
      // recovered edits must be rebased onto it, and Discard must return to server state.
      if (!state.openPolicy.baseline) state.openPolicy.baseline = clone(payload.baseline);
      state.openPolicy.draft = clone(payload.draft);
      state.openPolicy.status = 'succeeded';
      state.delta = clone(payload.delta);
      state.save.mutationId = payload.mutationId;
      state.save.fingerprint = payload.fingerprint;
      state.recovery = { restored: true };
    },
  },
});

export const {
  scopeOpened,
  catalogRequested,
  catalogSucceeded,
  catalogFailed,
  policyRequested,
  policySucceeded,
  policyFailed,
  draftPatched,
  facetTypeChanged,
  valuePolicyPatched,
  valuesPageRequested,
  valuesPageSucceeded,
  valuesPageFailed,
  assignmentChanged,
  assignmentCleared,
  groupDeleted,
  assignmentsClearedAll,
  saveRequested,
  saveSucceeded,
  saveFailed,
  saveErrorDismissed,
  conflictRebased,
  discarded,
  recoveryRestored,
} = slice.actions;

export function selectIsDirty(state) {
  if (!state.openPolicy.baseline || !state.openPolicy.draft) return false;
  const policyDirty = Object.keys(changedPolicy(
    state.openPolicy.draft,
    state.openPolicy.baseline.policy,
  )).length > 0;
  return policyDirty
    || Object.keys(state.delta.assignmentChanges).length > 0
    || Object.keys(state.delta.clearAssignmentIds).length > 0
    || Object.keys(state.delta.deleteGroupIds).length > 0
    || state.delta.clearAllAssignments;
}

export const selectShouldWarnUnsaved = selectIsDirty;

export function selectSavePayload(state) {
  if (!state.save.mutationId) return null;
  return { ...mutationCore(state), client_mutation_id: state.save.mutationId };
}

export function selectPagedValues(state) {
  const seen = {};
  const result = [];
  state.values.pageOrder.forEach((pageKey) => {
    (state.values.pages[pageKey] || []).forEach((id) => {
      if (seen[id]) return;
      seen[id] = true;
      const item = clone(state.values.entities[id]);
      if (state.delta.clearAllAssignments || state.delta.clearAssignmentIds[id]) {
        item.canonical_value = null;
        item.assignment_state = 'unassigned';
      }
      if (own(state.delta.assignmentChanges, id)) {
        item.canonical_value = state.delta.assignmentChanges[id];
        item.assignment_state = 'assigned';
      }
      result.push(item);
    });
  });
  return result;
}

export function facetPolicyDraftRecoveryKey(scope) {
  if (!validScope(scope)) return null;
  return `kea.facets.draft.v${FACET_DRAFT_RECOVERY_VERSION}.${scope.shop}.${scope.language}.${scope.document_type}`;
}

export function serializeFacetPolicyDraft(state) {
  if (!selectIsDirty(state) || !validScope(state.scope)) return null;
  return JSON.stringify({
    version: FACET_DRAFT_RECOVERY_VERSION,
    scope: clone(state.scope),
    facetKey: state.openPolicy.facetKey,
    baseline: {
      policy: sanitizePolicy(state.openPolicy.baseline.policy),
      revision: state.openPolicy.baseline.revision,
    },
    draft: sanitizePolicy(state.openPolicy.draft),
    delta: clone(state.delta),
    mutationId: state.save.mutationId,
    fingerprint: state.save.fingerprint,
  });
}

export function parseFacetPolicyDraft(raw, scope) {
  try {
    const value = JSON.parse(raw);
    if (!value || value.version !== FACET_DRAFT_RECOVERY_VERSION
      || !sameScope(value.scope, scope)
      || typeof value.facetKey !== 'string'
      || !value.baseline || typeof value.baseline.revision !== 'number'
      || !value.delta || typeof value.delta !== 'object') return null;
    return {
      version: value.version,
      scope: clone(value.scope),
      facetKey: value.facetKey,
      baseline: {
        policy: sanitizePolicy(value.baseline.policy),
        revision: value.baseline.revision,
      },
      draft: sanitizePolicy(value.draft),
      delta: {
        assignmentChanges: clone(value.delta.assignmentChanges || {}),
        clearAssignmentIds: clone(value.delta.clearAssignmentIds || {}),
        deleteGroupIds: clone(value.delta.deleteGroupIds || {}),
        clearAllAssignments: value.delta.clearAllAssignments === true,
      },
      mutationId: typeof value.mutationId === 'string' ? value.mutationId : null,
      fingerprint: typeof value.fingerprint === 'string' ? value.fingerprint : null,
    };
  } catch (error) {
    return null;
  }
}

export { initialState };
export default slice.reducer;
